import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import DeskMap from '../components/DeskMap';
import HeaderBar from '../components/HeaderBar';
import ErrorBoundary from '../components/ErrorBoundary';
import './BookingWizard.css';
import deskIcon from '../assets/desk-icon.svg';

const formatDisplayDate = (dateString) => {
    if (!dateString) {
        return '';
    }
    const parsedDate = new Date(`${dateString}T00:00:00`);
    if (Number.isNaN(parsedDate.getTime())) {
        return dateString;
    }
    return parsedDate.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

// Helper: Get YYYY-MM-DD string using LOCAL time (avoids UTC timezone shift)
const getLocalDateStr = (date) => {
    const d = date || new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function BookingWizard({ user, onLogout }) {
    const [step, setStep] = useState('booking-type'); // booking-type, time-slot, duration, dates, desk-selection, confirm, ticket
    const [bookingType, setBookingType] = useState(null); // 'half-day' or 'full-day'
    const [timeSlot, setTimeSlot] = useState(null); // '07:00-12:00' or '12:00-17:00'
    const [duration, setDuration] = useState(null); // 'one-day' or 'multiple-days'
    const [selectedDates, setSelectedDates] = useState([]);
    const [desks, setDesks] = useState([]);
    const [deskAssignments, setDeskAssignments] = useState({}); // date -> desk selection
    const [selectedDesk, setSelectedDesk] = useState(null);
    const [currentDateIndex, setCurrentDateIndex] = useState(0);
    // const [bookings, setBookings] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [bookingsByDate, setBookingsByDate] = useState({});
    const [holidays, setHolidays] = useState([]);
    const [showHolidayModal, setShowHolidayModal] = useState(null);
    const [occupancyRates, setOccupancyRates] = useState({});

    // Stats for today
    const [unitOccupancy, setUnitOccupancy] = useState(null);
    const [hotDeskOccupancy, setHotDeskOccupancy] = useState(null);
    const [occupiedCount, setOccupiedCount] = useState(0);
    const [unitTotalDesks, setUnitTotalDesks] = useState(0);
    const [unitAvailableDesks, setUnitAvailableDesks] = useState(0);
    const [totalDesks, setTotalDesks] = useState(0);
    const [totalAvailableDesks, setTotalAvailableDesks] = useState(0);
    const [totalBookedDesks, setTotalBookedDesks] = useState(0);
    const [nextBookings, setNextBookings] = useState([]);
    const [nextStats, setNextStats] = useState([]); // per-day detailed stats (unit/total counts)
    const [selectedHero, setSelectedHero] = useState(null); // 'unit'|'total'|'booked' or null
    const [deskBookingStatus, setDeskBookingStatus] = useState({}); // desk + date -> status
    const [bookedDesksByUnit, setBookedDesksByUnit] = useState([]); // details for booked modal
    const [isFunMode, setIsFunMode] = useState(false); // Default Fun Mode OFF



    console.log("DEBUG: BookingWizard v4 loaded - Notification Prefs Added");

    const navigate = useNavigate();
    const location = useLocation();

    // Reset booking wizard helper to clear booking state and return to first step
    const resetWizard = () => {
        setStep('booking-type');
        setBookingType(null);
        setTimeSlot(null);
        setDuration(null);
        setSelectedDates([]);
        setDesks([]);
        setDeskAssignments({});
        setCurrentDateIndex(0);
        setSelectedDesk(null);
        setError('');
        setSuccess('');
        setBookingsByDate({});
        setDeskBookingStatus({});
        setDeskBookingStatus({});
        setSelectedHero(null);
    };



    // const fetchBookings = useCallback(async () => {
    //     try {
    //         const response = await axios.get(`/api/bookings/user/${user.email}`);
    //         setBookings(response.data);
    //     } catch (err) {
    //         console.error('Failed to load bookings');
    //     }
    // }, [user.email]);

    // useEffect(() => {
    //     fetchBookings();
    // }, [fetchBookings]);

    const loadBookingsForDate = useCallback(
        async (date) => {
            if (!date) return;
            // Always reload to get fresh data
            try {
                const response = await axios.get(`/api/bookings/date/${date}`);
                const bookingsData = Array.isArray(response.data) ? response.data : [];
                console.log(`Loaded ${bookingsData.length} bookings for ${date}:`, bookingsData);
                setBookingsByDate((prev) => ({ ...prev, [date]: bookingsData }));
            } catch (err) {
                console.error('Failed to load bookings for date', date, err);
                setBookingsByDate((prev) => ({ ...prev, [date]: [] }));
            }
        },
        []
    );

    const fetchDesks = async (datesForQuery = selectedDates) => {
        try {
            setLoading(true);
            // Get ALL desks for display (show complete office map)
            const response = await axios.get('/api/desks');
            const allDesks = response.data;
            console.log(`Loaded ${allDesks.length} total desks for office map`);

            // NOTE: We now show ALL desks on the map
            // Booking restriction by unit is handled in handleDeskSelect and DeskMap click handler
            setDesks(allDesks);

            const primaryDate = (datesForQuery && datesForQuery[0]) || selectedDates[0];
            if (primaryDate) {
                await loadBookingsForDate(primaryDate);
            }

            if (datesForQuery && datesForQuery.length > 1) {
                await Promise.all(
                    datesForQuery.slice(1).map(async (date) => loadBookingsForDate(date))
                );
            }
        } catch (err) {
            console.error('Failed to load desks:', err);
            setError('Failed to load desks');
        } finally {
            setLoading(false);
        }
    };

    // When navigation includes resetWizard flag (from HeaderBar logo click), clear the wizard
    useEffect(() => {
        try {
            if (location?.state?.resetWizard) {
                console.log('BookingWizard: reset requested via navigation state');
                resetWizard();
                // clear the state so repeated navigations don't keep firing. Use replace to avoid new history entry
                navigate(location.pathname, { replace: true, state: {} });
            }
        } catch (e) {
            // no-op
        }
    }, [location?.state?.resetWizard]);

    const fetchHolidays = useCallback(async () => {
        try {
            const response = await axios.get('/api/holidays');
            setHolidays(response.data || []);
        } catch (err) {
            console.error('Failed to load holidays:', err);
            setHolidays([]);
        }
    }, []);

    const fetchOccupancyRates = useCallback(async (dates) => {
        try {
            const response = await axios.post('/api/occupancy-rates', {
                dates: dates,
                userUnit: user.unit
            });
            setOccupancyRates(response.data || {});
        } catch (err) {
            console.error('Failed to load occupancy rates:', err);
            setOccupancyRates({});
        }
    }, [user.unit]);

    const fetchStats = useCallback(async (date) => {
        try {
            // Get occupancy rates for today
            const resp = await axios.post('/api/occupancy-rates', {
                dates: [date],
                userUnit: user.unit
            });
            const data = resp.data || {};
            const dateData = data[date] || {};
            setUnitOccupancy(dateData.unitOccupancy || 0);
            setHotDeskOccupancy(dateData.hotDeskOccupancy || 0);

            // Get unique users booked for today and counts
            const bookingsResp = await axios.get(`/api/bookings/date/${date}`);
            const bookings = Array.isArray(bookingsResp.data) ? bookingsResp.data : [];
            const uniqueUsers = new Set(bookings.filter(b => b.status === 'confirmed').map(b => b.user_email));
            setOccupiedCount(uniqueUsers.size);

            // Also calculate totals and availability counts like DashboardPage
            const allDesksResp = await axios.get('/api/desks');
            const allDesks = Array.isArray(allDesksResp.data) ? allDesksResp.data : [];
            setTotalDesks(allDesks.length);

            const bookedForToday = bookings.filter(b => b.status === 'confirmed');
            setTotalBookedDesks(bookedForToday.length);

            setTotalAvailableDesks(Math.max(0, allDesks.length - bookedForToday.length));

            // Unit specific: desks already filtered in fetchDesks if user.unit, but compute from the full list
            const unitList = allDesks.filter(d => d.unit === user.unit);
            const myUnitDeskIds = new Set(unitList.map(d => d.id));
            const unitBookedCount = bookedForToday.filter(b => myUnitDeskIds.has(b.desk_id)).length;
            setUnitTotalDesks(unitList.length);
            setUnitAvailableDesks(Math.max(0, unitList.length - unitBookedCount));

            // Also compute next 4 workdays booking totals (workdays only)
            const nextDays = [];
            let d = new Date();
            // start from tomorrow
            d.setDate(d.getDate() + 1);
            while (nextDays.length < 4) {
                const dow = d.getDay(); // 0 Sun, 6 Sat
                if (dow !== 0 && dow !== 6) {
                    nextDays.push(new Date(d));
                }
                d.setDate(d.getDate() + 1);
            }

            const bookingsForDays = await Promise.all(nextDays.map(async (dt) => {
                const iso = getLocalDateStr(dt);
                try {
                    const r = await axios.get(`/api/bookings/date/${iso}`);
                    const list = Array.isArray(r.data) ? r.data : [];
                    const confirmed = list.filter(b => b.status === 'confirmed').length;
                    return { date: iso, confirmed };
                } catch (e) {
                    return { date: iso, confirmed: 0 };
                }
            }));

            // create a detailed per-date structure (total booked + unit booked)
            const stats = bookingsForDays.map(b => {
                const unitBooked = b.confirmed_for_unit || 0; // computed below if available
                return {
                    date: b.date,
                    label: new Date(b.date).toLocaleDateString('en-US', { weekday: 'long' }),
                    totalBooked: b.confirmed,
                    unitBooked: b.confirmed_for_unit || 0,
                    totalDesks: allDesks.length,
                    unitTotalDesks: unitList.length
                };
            });

            // If backend didn't return unit-specific counts, compute from bookingsForDays (we'll re-fetch per day above and attach confirmed_for_unit)
            // bookingsForDays returns {date, confirmed} and we will attempt to compute unit-specific count by re-querying per-date list
            const statsWithUnit = await Promise.all(stats.map(async s => {
                try {
                    const r = await axios.get(`/api/bookings/date/${s.date}`);
                    const list = Array.isArray(r.data) ? r.data : [];
                    const unitBookedCount = list.filter(b => b.status === 'confirmed' && myUnitDeskIds.has(b.desk_id)).length;
                    return { ...s, unitBooked: unitBookedCount };
                } catch (e) {
                    return s;
                }
            }));

            setNextStats(statsWithUnit);
            setNextBookings(statsWithUnit.map(b => ({ date: b.date, label: b.label.toLowerCase(), booked: b.totalBooked, total: b.totalDesks })));

            // Calculate booked desks by unit for the booked modal
            const allUnits = [...new Set(allDesks.map(d => d.unit))].sort();
            const bookedByUnit = allUnits.map(unitName => {
                const unitDesks = allDesks.filter(d => d.unit === unitName);
                const unitDesksIds = new Set(unitDesks.map(d => d.id));
                const bookedCount = bookedForToday.filter(b => unitDesksIds.has(b.desk_id)).length;
                return {
                    unit: unitName,
                    total: unitDesks.length,
                    booked: bookedCount,
                    available: Math.max(0, unitDesks.length - bookedCount)
                };
            });
            setBookedDesksByUnit(bookedByUnit);
        } catch (err) {
            console.error('Failed to load stats:', err);
            setUnitOccupancy(null);
            setHotDeskOccupancy(null);
            setOccupiedCount(0);
        }
    }, [user.unit]);

    useEffect(() => {
        fetchHolidays();
        // Fetch today's stats on mount
        const today = getLocalDateStr();
        fetchStats(today);
    }, [fetchHolidays, fetchStats]);

    useEffect(() => {
        // Fetch occupancy rates and holidays for the next 14 days whenever step changes to date selection.
        // Also poll periodically and re-fetch on window focus so admin changes (add/remove holidays)
        // are reflected without requiring a full page refresh.
        if (step === 'date-selection' || step === 'dates-selection') {
            const dates = Array.from({ length: 14 }).map((_, i) => {
                const date = new Date();
                date.setDate(date.getDate() + i);
                return getLocalDateStr(date);
            });

            // One-off immediate fetch
            fetchHolidays();
            fetchOccupancyRates(dates);

            // Polling (lightweight) every 15s while on the date selection screen
            const pollingInterval = setInterval(() => {
                fetchHolidays();
                fetchOccupancyRates(dates);
            }, 15000);

            // Re-fetch when the user focuses the tab/window
            const handleFocus = () => {
                fetchHolidays();
                fetchOccupancyRates(dates);
            };
            window.addEventListener('focus', handleFocus);

            return () => {
                clearInterval(pollingInterval);
                window.removeEventListener('focus', handleFocus);
            };
        }
        return undefined;
    }, [step, fetchOccupancyRates, fetchHolidays]);

    // Auto-refresh desks on window focus (when user returns to tab)
    useEffect(() => {
        if (step === 'desk-selection' && selectedDates.length > 0) {
            // Refresh on window focus
            const handleFocus = () => fetchDesks(selectedDates);
            window.addEventListener('focus', handleFocus);

            return () => {
                window.removeEventListener('focus', handleFocus);
            };
        }
    }, [step, selectedDates]);

    const getStepNumber = (stepKey) => {
        let sequence;
        if (bookingType === 'half-day') {
            sequence = ['booking-type', 'time-slot', 'desk-selection', 'confirm', 'ticket'];
        } else if (duration === 'multiple-days') {
            sequence = ['booking-type', 'duration', 'dates-selection', 'desk-selection', 'confirm', 'ticket'];
        } else if (duration === 'one-day') {
            sequence = ['booking-type', 'duration', 'date-selection', 'desk-selection', 'confirm', 'ticket'];
        } else {
            sequence = ['booking-type', 'duration', 'desk-selection', 'confirm', 'ticket'];
        }

        const index = sequence.indexOf(stepKey);
        return index >= 0 ? index + 1 : null;
    };

    const getStepTitle = (stepKey, title) => {
        const number = getStepNumber(stepKey);
        return number ? `Step ${number}: ${title}` : title;
    };

    const handleBookingTypeSelect = (type) => {
        setBookingType(type);
        setSelectedDates([]);
        setDeskAssignments({});
        setCurrentDateIndex(0);
        setError('');
        setSuccess('');
        if (type === 'half-day') {
            setStep('time-slot');
        } else {
            setStep('duration');
        }
    };

    const handleTimeSlotSelect = (slot) => {
        setTimeSlot(slot);
        setSelectedDates([]);
        setDeskAssignments({});
        setCurrentDateIndex(0);
        setError('');
        setSuccess('');
        setStep('date-selection');
    };

    const handleDurationSelect = (dur) => {
        setDuration(dur);
        setSelectedDates([]);
        setDeskAssignments({});
        setCurrentDateIndex(0);
        setError('');
        setSuccess('');
        setStep(dur === 'one-day' ? 'date-selection' : 'dates-selection');
    };

    const handleDateSelect = (date) => {
        if (!date) return;
        // enforce 4 total active bookings limit for single date selections
        if (!user?.is_admin) {
            try {
                const today = new Date().toISOString().split('T')[0];

                axios.get(`/api/bookings/user/${user.email}`).then(resp => {
                    const bookings = Array.isArray(resp.data) ? resp.data : [];
                    // Count total active future bookings
                    const activeCount = bookings.filter(b =>
                        b.status === 'confirmed' && b.booking_date >= today
                    ).length;
                    if (activeCount >= 4) {
                        setError(`Booking limit reached (4 active bookings). Cancel an existing booking to book a new date.`);
                        return;
                    }
                    // Continue selecting the date
                    setSelectedDates([date]);
                    setDeskAssignments({});
                    setCurrentDateIndex(0);
                    setError('');
                    setSuccess('');
                    fetchDesks([date]);
                    setStep('desk-selection');
                }).catch(err => {
                    console.error('Failed to validate date selection', err);
                    // fallback to allowing the selection
                    setSelectedDates([date]);
                    setDeskAssignments({});
                    setCurrentDateIndex(0);
                    setError('');
                    setSuccess('');
                    fetchDesks([date]);
                    setStep('desk-selection');
                });

            } catch (err) {
                console.error('handleDateSelect error', err);
                return;
            }

            return; // prevent the old immediate selection because above handles it asynchronously
        }
        setSelectedDates([date]);
        setDeskAssignments({});
        setCurrentDateIndex(0);
        setError('');
        setSuccess('');
        fetchDesks([date]);
        setStep('desk-selection');
    };

    const handleMultipleDatesSelect = async (dates) => {
        // Allow admin to bypass max-days restriction
        if (!user?.is_admin && dates.length > 4) {
            setError('Maximum 4 bookings allowed at a time');
            return;
        }

        // Users can book across weeks as long as they stay within their quota
        // (The backend enforces the quota limit)

        // Enforce total active bookings limit: user can have max 4 active future bookings
        if (!user?.is_admin) {
            try {
                const today = new Date().toISOString().split('T')[0];
                const me = await axios.get(`/api/bookings/user/${user.email}`);
                const myBookings = Array.isArray(me.data) ? me.data : [];
                // Count existing active future bookings
                const existingActiveBookings = myBookings.filter(b =>
                    b.status === 'confirmed' && b.booking_date >= today
                ).length;
                const requestedCount = dates.length;
                if ((existingActiveBookings + requestedCount) > 4) {
                    setError(`Booking limit exceeded: you have ${existingActiveBookings} active booking(s). Maximum 4 allowed.`);
                    return;
                }
            } catch (err) {
                console.error('Failed to validate booking limit', err);
                // continue silently if validation cannot be completed
            }
        }
        setError('');
        const uniqueSortedDates = Array.from(new Set(dates)).sort();
        setSelectedDates(uniqueSortedDates);
        setDeskAssignments((prev) => {
            const nextAssignments = {};
            uniqueSortedDates.forEach((date) => {
                if (prev[date]) {
                    nextAssignments[date] = prev[date];
                }
            });
            return nextAssignments;
        });
        setCurrentDateIndex(0);
    };

    const handleMultipleDatesContinue = () => {
        if (selectedDates.length < 2) {
            setError('Please select at least two dates');
            return;
        }
        setError('');
        setSuccess('');
        setCurrentDateIndex(0);
        fetchDesks(selectedDates);
        setStep('desk-selection');
    };

    const handleDeskSelect = (desk) => {
        const activeDates = selectedDates.length > 0 ? selectedDates : [getLocalDateStr()];
        const targetDate = activeDates[currentDateIndex] || activeDates[0];

        if (!targetDate) {
            return;
        }

        // Check if desk belongs to user's allowed units (primary + additional)
        const userUnits = [user.unit];
        if (user.additional_units) {
            userUnits.push(...user.additional_units.split(',').map(u => u.trim()).filter(Boolean));
        }
        if (user.unit && !userUnits.includes(desk.unit)) {
            setError(`You can only book desks from your units (${userUnits.join(', ')}). Desk ${desk.id} belongs to ${desk.unit}.`);
            return;
        }

        // Check if desk is already booked for this date
        const bookingsForTargetDate = bookingsByDate[targetDate] || [];
        const existingBooking = bookingsForTargetDate.find((b) => b.desk_id === desk.id);

        if (existingBooking && existingBooking.user_email !== user.email) {
            setError(`Desk ${desk.id} is already booked by another user for ${formatDisplayDate(targetDate)}`);
            return;
        }

        const userMonitorPref = user.monitor_preference || 'Single Monitor';
        const normalizedPreference = userMonitorPref.toLowerCase();
        const isAnyPreference = normalizedPreference.includes('any');
        const preferredKeyword = normalizedPreference.includes('dual') ? 'dual' : 'single';
        const monitorSetup = (desk.monitor_setup || 'Single Monitor').toLowerCase();
        const matchesPreference = isAnyPreference || monitorSetup.includes(preferredKeyword);
        const displayPreference = userMonitorPref === 'Any' ? 'Any Available' : userMonitorPref;

        // Check if desk has adjustable monitor and show warning
        const hasAdjustableMonitor = desk.adjustable_monitor === 1 || desk.adjustable_monitor === true;
        console.log(`Desk ${desk.id} - adjustable_monitor:`, desk.adjustable_monitor, 'hasAdjustableMonitor:', hasAdjustableMonitor);

        if (hasAdjustableMonitor) {
            const userConfirmed = window.confirm(
                `⚠️ ACCESSIBILITY NOTICE\n` +
                `This desk has an adjustable monitor for accessibility needs.\n` +
                `Your booking may be automatically reassigned to another desk in your unit ` +
                `if a colleague with accessibility requirements books this desk.\n` +
                `You will be notified by email if reassignment occurs.\n` +
                `Do you want to proceed with booking this desk?`
            );

            if (!userConfirmed) {
                return; // User cancelled, don't proceed with booking
            }
        }

        setDeskAssignments((prev) => ({
            ...prev,
            [targetDate]: desk,
        }));
        setError('');
        if (isAnyPreference) {
            setSuccess(`Desk ${desk.id} selected for ${formatDisplayDate(targetDate)} (${desk.monitor_setup || 'Single Monitor'})`);
        } else if (matchesPreference) {
            setSuccess(`Desk ${desk.id} selected for ${formatDisplayDate(targetDate)} (${desk.monitor_setup || 'Single Monitor'})`);
        } else {
            setSuccess(`Desk ${desk.id} selected for ${formatDisplayDate(targetDate)}. Preferred ${displayPreference} not available; using ${desk.monitor_setup || 'Single Monitor'}.`);
        }

        const hasMultipleDays = activeDates.length > 1;
        const isLastDate = currentDateIndex >= activeDates.length - 1;

        if (hasMultipleDays && !isLastDate) {
            const nextDate = activeDates[currentDateIndex + 1];
            if (nextDate) {
                loadBookingsForDate(nextDate);
            }
            setCurrentDateIndex((prev) => prev + 1);
        } else {
            setCurrentDateIndex(activeDates.length - 1);
            setStep('confirm');
        }
    };

    const handleConfirmBooking = async () => {
        if (selectedDates.length === 0) {
            setError('Please choose at least one booking date');
            return;
        }

        const missingAssignments = selectedDates.filter((date) => !deskAssignments[date]);
        if (missingAssignments.length > 0) {
            setError('Please select a desk for every chosen date before confirming.');
            return;
        }

        try {
            setError('');
            setLoading(true);



            if (selectedDates.length === 1) {
                // Single booking - use existing endpoint
                const desk = deskAssignments[selectedDates[0]];
                await axios.post('/api/bookings', {
                    desk_id: desk.id,
                    user_email: user.email,
                    booking_date: selectedDates[0],
                    start_time: timeSlot ? timeSlot.split('-')[0] : '07:00',
                    end_time: timeSlot ? timeSlot.split('-')[1] : '17:00',
                });
            } else {
                // Multiple bookings - use new consolidated endpoint
                const bookings = selectedDates.map(date => {
                    const desk = deskAssignments[date];
                    return {
                        desk_id: desk.id,
                        booking_date: date,
                        start_time: timeSlot ? timeSlot.split('-')[0] : '07:00',
                        end_time: timeSlot ? timeSlot.split('-')[1] : '17:00',
                    };
                });

                await axios.post('/api/bookings/multiple', {
                    bookings: bookings,
                    user_email: user.email,
                });
            }

            setSuccess('Booking confirmed!');
            // await fetchBookings();
            setStep('ticket');
        } catch (err) {
            setError(err.response?.data?.error || 'Booking failed');
        } finally {
            setLoading(false);
        }
    };

    const generateTicket = () => {
        const firstDate = selectedDates[0];
        const lastDate = selectedDates[selectedDates.length - 1];
        const bookingRef = new Date().toISOString().replace(/[-:.]/g, '').slice(0, 15);

        return {
            employeeName: user.name || user.email,
            unit: user.unit,
            duration: bookingType === 'half-day' ? 'Half day' : 'Full day',
            desksReserved: selectedDates.length,
            fromDate: firstDate,
            toDate: lastDate,
            assignments: selectedDates.map(date => ({
                date,
                desk: deskAssignments[date]?.id || '-',
                zone: deskAssignments[date]?.zone || 'N/A',
                monitor: deskAssignments[date]?.monitor_setup || 'Single'
            })),
            bookingRef,
            timeSlot: timeSlot || 'Full day'
        };
    };

    const handlePrint = () => {
        const ticketData = generateTicket();
        const printWindow = window.open('', '', 'width=900,height=600');
        printWindow.document.write(`
            <html>
                <head>
                    <title>UNICEF Indonesia - Desk Booking Ticket</title>
                    <style>
                        body { 
                            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
                            margin: 0;
                            padding: 20px; 
                            background: #f5f5f5;
                        }
                        .print-ticket {
                            background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
                            border-radius: 20px;
                            box-shadow: 0 20px 40px rgba(30, 60, 114, 0.3);
                            overflow: hidden;
                            max-width: 800px;
                            margin: 0 auto;
                            display: flex;
                        }
                        .print-left {
                            flex: 1;
                            padding: 40px;
                            color: white;
                        }
                        .print-right {
                            flex: 1.2;
                            padding: 40px;
                            background: white;
                            color: #333;
                        }
                        .employee-name { font-size: 24px; font-weight: bold; text-transform: uppercase; margin-bottom: 5px; }
                        .employee-unit { font-size: 16px; opacity: 0.9; text-transform: uppercase; letter-spacing: 2px; }
                        .label { font-size: 12px; opacity: 0.8; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 5px; }
                        .value { font-size: 18px; font-weight: bold; }
                        .value.large { font-size: 32px; font-weight: 900; }
                        .date-range { display: flex; gap: 30px; margin: 25px 0; }
                        .date-value { font-size: 20px; font-weight: bold; }
                        .unicef-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #e0e8f0; }
                        .unicef-text { font-size: 18px; font-weight: bold; color: #1e3c72; letter-spacing: 3px; }
                        .assignment-table { margin-bottom: 25px; }
                        .table-header { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 15px; padding: 12px 0; border-bottom: 2px solid #1e3c72; margin-bottom: 10px; }
                        .col-header { font-size: 12px; font-weight: bold; color: #1e3c72; text-transform: uppercase; }
                        .table-row { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 15px; padding: 8px 0; border-bottom: 1px solid #e0e8f0; }
                        .col-data { font-size: 14px; color: #333; font-weight: 500; }
                        .ref-value { font-size: 16px; font-weight: bold; color: #1e3c72; font-family: 'Courier New', monospace; }
                        .ticket-footer { text-align: center; font-size: 12px; color: #666; margin-top: 20px; padding-top: 20px; border-top: 1px solid #e0e8f0; }
                        @media print { body { background: white; } .print-ticket { box-shadow: none; } }
                    </style>
                </head>
                <body>
                    <div class="print-ticket">
                        <div class="print-left">
                            <div class="employee-name">${ticketData.employeeName}</div>
                            <div class="employee-unit">${ticketData.unit}</div>
                            <div style="margin: 30px 0;">
                                <div class="label">Duration</div>
                                <div class="value">${ticketData.duration}</div>
                            </div>
                            <div style="margin: 25px 0;">
                                <div class="label">Desk Reserved</div>
                                <div class="value large">${ticketData.desksReserved}</div>
                            </div>
                            <div class="date-range">
                                <div>
                                    <div class="label">From</div>
                                    <div class="date-value">${new Date(ticketData.fromDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                                </div>
                                <div>
                                    <div class="label">To</div>
                                    <div class="date-value">${new Date(ticketData.toDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                                </div>
                            </div>
                        </div>

                        
                        <div class="print-right">
                            <div class="unicef-header">
                                <div class="unicef-text">UNICEF INDONESIA</div>
                                <img src="/data/UNICEF_Logo.png" 
                                     alt="UNICEF Logo" 
                                     style="height: 40px; width: auto;" />
                            </div>
                            <div class="assignment-table">
                                <div class="table-header">
                                    <div class="col-header">Date</div>
                                    <div class="col-header">Desk</div>
                                    <div class="col-header">Zone</div>
                                    <div class="col-header">Monitor</div>
                                </div>
                                ${ticketData.assignments.map(assignment => `
                                    <div class="table-row">
                                        <div class="col-data">${new Date(assignment.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                                        <div class="col-data">${assignment.desk}</div>
                                        <div class="col-data">${assignment.zone}</div>
                                        <div class="col-data">${assignment.monitor}</div>
                                    </div>
                                `).join('')}
                            </div>
                            <div style="background: #f0f7ff; padding: 15px; border-radius: 8px; border-left: 4px solid #1e3c72; margin-bottom: 20px;">
                                <div style="font-size: 12px; color: #666; text-transform: uppercase; margin-bottom: 5px;">Booking Reference:</div>
                                <div class="ref-value">${ticketData.bookingRef}</div>
                            </div>
                            <div class="ticket-footer">
                                Please keep this ticket for your records
                            </div>
                        </div>
                    </div>
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    const handleBack = () => {
        if (step === 'booking-type') {
            navigate('/dashboard');
        } else if (step === 'time-slot') {
            setStep('booking-type');
        } else if (step === 'duration') {
            setStep('booking-type');
        } else if (step === 'date-selection') {
            if (bookingType === 'half-day') {
                setStep('time-slot');
            } else {
                setStep('duration');
            }
        } else if (step === 'dates-selection') {
            setStep('duration');
        } else if (step === 'desk-selection') {
            if (selectedDates.length > 1 && currentDateIndex > 0) {
                setCurrentDateIndex((prev) => prev - 1);
            } else if (bookingType === 'half-day') {
                setStep('date-selection');
            } else if (duration === 'one-day') {
                setStep('date-selection');
            } else {
                setStep('dates-selection');
            }
        } else if (step === 'confirm') {
            setStep('desk-selection');
            setCurrentDateIndex(Math.max(selectedDates.length - 1, 0));
        } else if (step === 'ticket') {
            setSelectedDates([]);
            setBookingType(null);
            setTimeSlot(null);
            setDuration(null);
            setDeskAssignments({});
            setCurrentDateIndex(0);
            setSuccess('');
            setError('');
            setStep('booking-type');
        }
    };

    // Check if user has unit assigned
    const hasUnit = Boolean(user && user.unit);
    const orderedDates = [...selectedDates];
    const activeDate = orderedDates[currentDateIndex] || orderedDates[0] || null;
    const allDatesAssigned = orderedDates.length > 0 && orderedDates.every((date) => deskAssignments[date]);
    const assignmentList = orderedDates.map((date, index) => ({
        date,
        desk: deskAssignments[date] || null,
        index,
    }));
    const assignedCount = assignmentList.filter((item) => item.desk).length;
    const activeDeskId = activeDate ? deskAssignments[activeDate]?.id : undefined;
    const deskSelectionDate = activeDate || getLocalDateStr();
    const userMonitorPref = user.monitor_preference || 'Single Monitor';
    const normalizedMonitorPreference = userMonitorPref.toLowerCase();
    const isAnyPreferenceSelected = normalizedMonitorPreference.includes('any');
    const monitorPreferenceKeyword = normalizedMonitorPreference.includes('dual') ? 'dual' : 'single';
    const hasPreferredMonitor =
        isAnyPreferenceSelected ||
        desks.some((desk) => (desk.monitor_setup || 'Single Monitor').toLowerCase().includes(monitorPreferenceKeyword));

    useEffect(() => {
        if (deskSelectionDate) {
            loadBookingsForDate(deskSelectionDate);
        }
    }, [deskSelectionDate, loadBookingsForDate]);

    return (
        <div className="booking-wizard">
            <HeaderBar user={user} onLogout={onLogout} />

            <div className="wizard-container">
                <ErrorBoundary>
                    {error && <div className="error">{error}</div>}
                    {success && <div className="success">{success}</div>}

                    {/* Today's Stats Card (only in Step 1) */}
                    {step === 'booking-type' && (
                        <div className="hero-occupancy hero-compact">
                            <div className="hero-inner">
                                <div className="hero-left">
                                    <h3>Today Occupancy</h3>
                                    <p className="hero-sub">Quick glance — unit & overall availability</p>
                                </div>

                                <div className="hero-stats">
                                    <div
                                        className={`hero-stat ${selectedHero === 'unit' ? 'active' : ''}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setSelectedHero(selectedHero === 'unit' ? null : 'unit')}
                                    >
                                        <div className="stat-icon">🏢</div>
                                        <div className="stat-body">
                                            <div className="stat-label">Desk Available In Your Unit</div>
                                            <div className="stat-value">{unitAvailableDesks} <span className="stat-sep">of</span> {unitTotalDesks}</div>
                                            <div className="stat-sub">Your unit — available desks</div>
                                        </div>
                                    </div>

                                    <div
                                        className={`hero-stat ${selectedHero === 'total' ? 'active' : ''}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setSelectedHero(selectedHero === 'total' ? null : 'total')}
                                    >
                                        <div className="stat-icon" aria-hidden="true">
                                            <img src={deskIcon} alt="desk" />
                                        </div>
                                        <div className="stat-body">
                                            <div className="stat-label">Total Desk Availability</div>
                                            <div className="stat-value">{totalAvailableDesks} <span className="stat-sep">of</span> {totalDesks}</div>
                                            <div className="stat-sub">All desks across office</div>
                                        </div>
                                    </div>

                                    <div
                                        className={`hero-stat ${selectedHero === 'booked' ? 'active' : ''}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setSelectedHero(selectedHero === 'booked' ? null : 'booked')}
                                    >
                                        <div className="stat-icon">📅</div>
                                        <div className="stat-body">
                                            <div className="stat-label">Booked today</div>
                                            <div className="stat-value">{totalBookedDesks}</div>
                                            <div className="stat-sub">Confirmed bookings for {new Date().toLocaleDateString()}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* removed inline next-bookings summary to keep hero slim; details appear in modal */}
                            </div>
                        </div>
                    )}

                    {/* Popup overlay for next-4-workdays details (shown when clicking Unit or Available (total)) */}
                    {selectedHero && (selectedHero === 'unit' || selectedHero === 'total') && (
                        <div className="next-popup-overlay" onClick={() => setSelectedHero(null)}>
                            <div className="next-popup" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                                <div className="popup-header">
                                    <h3 className="popup-title">{selectedHero === 'unit' ? 'Unit availability' : 'Available (total)'} — next 4 workdays</h3>
                                    <button className="popup-close" onClick={() => setSelectedHero(null)}>×</button>
                                </div>
                                <ul className="next-list popup-list">
                                    {nextStats.map((s) => (
                                        <li key={s.date} className="next-item popup-item">
                                            <div className="next-day">{new Date(s.date).toLocaleDateString('en-US', { weekday: 'long' })}</div>
                                            <div className="next-count popup-count">
                                                {selectedHero === 'unit' ? `${Math.max(0, (s.unitTotalDesks || 0) - (s.unitBooked || 0))} available of ${s.unitTotalDesks || 0}` : `${Math.max(0, (s.totalDesks || 0) - (s.totalBooked || 0))} available of ${s.totalDesks || 0}`}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                <div className="popup-footer">
                                    <button className="btn-clear-selection" onClick={() => setSelectedHero(null)}>Close</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Popup overlay for Booked Today details (desk allocation by unit) */}
                    {selectedHero === 'booked' && (
                        <div className="booked-popup-overlay" onClick={() => setSelectedHero(null)}>
                            <div className="booked-popup" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                                <div className="popup-header">
                                    <h3 className="popup-title">Desk Allocation — Today</h3>
                                    <button className="popup-close" onClick={() => setSelectedHero(null)}>×</button>
                                </div>
                                <div className="booked-content">
                                    <div className="booked-summary">
                                        <div className="summary-item">
                                            <span className="summary-label">Total Booked</span>
                                            <span className="summary-value">{totalBookedDesks}</span>
                                        </div>
                                        <div className="summary-divider"></div>
                                        <div className="summary-item">
                                            <span className="summary-label">Total Available</span>
                                            <span className="summary-value">{totalAvailableDesks}</span>
                                        </div>
                                        <div className="summary-divider"></div>
                                        <div className="summary-item">
                                            <span className="summary-label">Total Desks</span>
                                            <span className="summary-value">{totalDesks}</span>
                                        </div>
                                    </div>
                                    <div className="booked-units">
                                        <h4 className="booked-subtitle">Breakdown by Unit</h4>
                                        <div className="units-list">
                                            {bookedDesksByUnit.length > 0 ? (
                                                bookedDesksByUnit.map((unitData) => (
                                                    <div key={unitData.unit} className="unit-row">
                                                        <div className="unit-name">{unitData.unit}</div>
                                                        <div className="unit-stats">
                                                            <div className="stat-badge booked-badge" title="Booked desks">
                                                                <span className="badge-value">{unitData.booked}</span>
                                                                <span className="badge-label">booked</span>
                                                            </div>
                                                            <div className="stat-badge available-badge" title="Available desks">
                                                                <span className="badge-value">{unitData.available}</span>
                                                                <span className="badge-label">available</span>
                                                            </div>
                                                            <div className="unit-progress">
                                                                <div className="progress-bar">
                                                                    <div
                                                                        className="progress-fill"
                                                                        style={{ width: `${(unitData.booked / unitData.total) * 100}%` }}
                                                                    ></div>
                                                                </div>
                                                                <span className="progress-text">{unitData.total} total</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <p className="empty-message">No units available</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="popup-footer">
                                    <button className="btn-clear-selection" onClick={() => setSelectedHero(null)}>Close</button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="wizard-content">
                        {!hasUnit && (
                            <div className="step alert-warning">
                                <h2>⚠️ Profile Incomplete</h2>
                                <p>Your unit assignment is not set. Please contact your administrator to assign your unit before booking a desk.</p>
                            </div>
                        )}

                        {/* Step 1: Booking Type */}
                        {step === 'booking-type' && hasUnit && (
                            <div className="step">
                                <h2>{getStepTitle('booking-type', 'Select Booking Type')}</h2>
                                <div className="options">
                                    <button
                                        onClick={() => handleBookingTypeSelect('half-day')}
                                        className="option-btn"
                                    >
                                        <div className="option-icon">⏰</div>
                                        <div className="option-title">Half Day</div>
                                        <div className="option-desc">Book morning or afternoon</div>
                                    </button>
                                    <button
                                        onClick={() => handleBookingTypeSelect('full-day')}
                                        className="option-btn"
                                    >
                                        <div className="option-icon">📆</div>
                                        <div className="option-title">Full Day</div>
                                        <div className="option-desc">Book entire day or multiple days</div>
                                    </button>
                                </div>
                                <div className="wizard-nav">
                                    <button onClick={handleBack} className="btn-back-step">
                                        Back to Book Page
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 2: Time Slot (Half Day) */}
                        {step === 'time-slot' && (
                            <div className="step">
                                <h2>{getStepTitle('time-slot', 'Select Time Slot')}</h2>
                                <div className="options">
                                    <button
                                        onClick={() => handleTimeSlotSelect('07:00-12:00')}
                                        className="option-btn"
                                    >
                                        <div className="option-icon">🌅</div>
                                        <div className="option-title">Morning</div>
                                        <div className="option-desc">07:00 - 12:00</div>
                                    </button>
                                    <button
                                        onClick={() => handleTimeSlotSelect('12:00-17:00')}
                                        className="option-btn"
                                    >
                                        <div className="option-icon">☀️</div>
                                        <div className="option-title">Afternoon</div>
                                        <div className="option-desc">12:00 - 17:00</div>
                                    </button>
                                </div>
                                <div className="wizard-nav">
                                    <button onClick={handleBack} className="btn-back-step">
                                        Back
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Duration (Full Day) */}
                        {step === 'duration' && (
                            <div className="step">
                                <h2>{getStepTitle('duration', 'Select Duration')}</h2>
                                <div className="options">
                                    <button
                                        onClick={() => handleDurationSelect('one-day')}
                                        className="option-btn"
                                    >
                                        <div className="option-icon">📅</div>
                                        <div className="option-title">One Day</div>
                                        <div className="option-desc">Single day booking</div>
                                    </button>
                                    <button
                                        onClick={() => handleDurationSelect('multiple-days')}
                                        className="option-btn"
                                    >
                                        <div className="option-icon">📚</div>
                                        <div className="option-title">Multiple Days</div>
                                        <div className="option-desc">Up to 4 days each Week</div>
                                    </button>
                                </div>
                                <div className="wizard-nav">
                                    <button onClick={handleBack} className="btn-back-step">
                                        Back
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Select Single Date */}
                        {step === 'date-selection' && (
                            <div className="step">
                                <h2>{getStepTitle('date-selection', 'Select Date')}</h2>
                                <div className="multi-date-container">
                                    <div className="date-picker-grid">
                                        {Array.from({ length: 14 }).map((_, i) => {
                                            const date = new Date();
                                            date.setDate(date.getDate() + i);
                                            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                                            const isSelected = selectedDates.includes(dateStr);

                                            // Check if this date is a holiday
                                            const holidaysList = Array.isArray(holidays) ? holidays : [];
                                            const holidayInfo = holidaysList.find(h => h.holiday_date === dateStr);
                                            const isHoliday = !!holidayInfo;
                                            const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });

                                            // Check if weekend
                                            const dayOfWeek = date.getDay();
                                            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

                                            // Check if past date
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            const isPast = date < today;
                                            const isDisabled = isPast || isWeekend;

                                            // Get real occupancy rates from API
                                            const ratesData = occupancyRates[dateStr] || {};
                                            const occupancyRate = ratesData.hotDeskOccupancy || 0;
                                            const unitOccupancyRate = ratesData.unitOccupancy || 0;

                                            let buttonClass = 'date-btn-large';
                                            if (isSelected) buttonClass += ' selected';
                                            if (isHoliday) buttonClass += ' holiday';
                                            if (isWeekend && !isHoliday) buttonClass += ' weekend';
                                            if (isDisabled) buttonClass += ' disabled';

                                            return (
                                                <button
                                                    key={i}
                                                    className={buttonClass}
                                                    onClick={() => {
                                                        if (isDisabled) return;
                                                        if (isHoliday) {
                                                            setShowHolidayModal({ show: true, date: dateStr, name: holidayInfo.name });
                                                        } else {
                                                            handleDateSelect(dateStr);
                                                        }
                                                    }}
                                                    disabled={isDisabled}
                                                >
                                                    <div className="date-btn-day">{dayName.toUpperCase()}</div>
                                                    <div className="date-btn-date-full">
                                                        <div className="date-number">{date.getDate()}</div>
                                                        <div className="date-month-year">{date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</div>
                                                    </div>
                                                    {!isHoliday && (
                                                        <div className="occupancy-info">
                                                            <div className="occupancy-item">
                                                                <div className="occupancy-label">Hot Desk Occupancy</div>
                                                                <div className="occupancy-value">{occupancyRate} %</div>
                                                            </div>
                                                            <div className="occupancy-item">
                                                                <div className="occupancy-label">Unit Occupancy Rate</div>
                                                                <div className="occupancy-value">{unitOccupancyRate} %</div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {isHoliday && (
                                                        <>
                                                            <div className="occupancy-info">
                                                                <div className="occupancy-item">
                                                                    <div className="occupancy-label">Hot Desk Occupancy</div>
                                                                    <div className="occupancy-value">{occupancyRate} %</div>
                                                                </div>
                                                                <div className="occupancy-item">
                                                                    <div className="occupancy-label">Unit Occupancy Rate</div>
                                                                    <div className="occupancy-value">{unitOccupancyRate} %</div>
                                                                </div>
                                                            </div>
                                                            <div className="date-btn-holiday-info" title={holidayInfo.name}>
                                                                ℹ️ {holidayInfo.name}
                                                            </div>
                                                        </>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {selectedDates.length > 0 && selectedDates.some(dateStr => {
                                        const ratesData = occupancyRates[dateStr] || {};
                                        const occupancyRate = ratesData.hotDeskOccupancy || 0;
                                        return occupancyRate >= 80;
                                    }) && (
                                            <div style={{
                                                backgroundColor: '#fff3cd',
                                                border: '1px solid #ffc107',
                                                borderRadius: '4px',
                                                padding: '12px',
                                                marginTop: '12px',
                                                color: '#856404',
                                                fontSize: '14px'
                                            }}>
                                                <strong>⚠️ High Demand:</strong> Some selected dates have occupancy ≥80%. These bookings may be added to a waiting list or flex pool.
                                            </div>
                                        )}
                                    <div className="selected-dates">
                                        <div>Selected: {selectedDates.length > 0 ? '1 day' : '0 days'}</div>
                                        {selectedDates.length > 0 && (
                                            <ol>
                                                {selectedDates.map((dateStr, index) => {
                                                    const [y, m, d] = dateStr.split('-').map(Number);
                                                    const date = new Date(y, m - 1, d);
                                                    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                                                    const month = date.toLocaleDateString('en-US', { month: 'short' });
                                                    const day = date.getDate();
                                                    const year = date.getFullYear();
                                                    return (
                                                        <li key={index}>{dayName}, {month} {day}, {year}</li>
                                                    );
                                                })}
                                            </ol>
                                        )}
                                    </div>
                                    <div className="multi-date-actions">
                                        <button onClick={handleBack} className="btn-back-step">
                                            Back
                                        </button>
                                    </div>
                                    <div className="occupancy-info-legend">
                                        <p><strong>Hot Desk Occupancy:</strong> Count whole hot desks in office</p>
                                        <p><strong>Unit Occupancy Rate:</strong> Only count hot desks assigned to your unit</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 5: Select Multiple Dates */}
                        {step === 'dates-selection' && (
                            <div className="step">
                                <h2>{getStepTitle('dates-selection', user && user.is_admin ? 'Select Dates (Admin - Unlimited)' : 'Select Dates (Max 4 Days Per Week)')}</h2>
                                <div className="multi-date-container">
                                    <div className="date-picker-grid">
                                        {Array.from({ length: 14 }).map((_, i) => {
                                            const date = new Date();
                                            date.setDate(date.getDate() + i);
                                            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                                            const isSelected = selectedDates.includes(dateStr);

                                            // Check if this date is a holiday (ensure holidays is an array)
                                            const holidaysList = Array.isArray(holidays) ? holidays : [];
                                            const holidayInfo = holidaysList.find(h => h.holiday_date === dateStr);
                                            const isHoliday = !!holidayInfo;
                                            const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });

                                            // Check if weekend (Saturday = 6, Sunday = 0)
                                            const dayOfWeek = date.getDay();
                                            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

                                            // Check if past date
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            const isPast = date < today;
                                            const isDisabled = isPast || isWeekend;

                                            // Get real occupancy rates from API
                                            const ratesData = occupancyRates[dateStr] || {};
                                            const occupancyRate = ratesData.hotDeskOccupancy || 0;
                                            const unitOccupancyRate = ratesData.unitOccupancy || 0;

                                            return (
                                                <button
                                                    key={dateStr}
                                                    onClick={async () => {
                                                        if (isDisabled) return;
                                                        if (isHoliday) {
                                                            setShowHolidayModal(holidayInfo);
                                                            return;
                                                        }
                                                        if (isSelected) {
                                                            await handleMultipleDatesSelect(selectedDates.filter(d => d !== dateStr));
                                                        } else {
                                                            await handleMultipleDatesSelect([...selectedDates, dateStr]);
                                                        }
                                                    }}
                                                    className={`date-btn-large ${isSelected ? 'selected' : ''} ${isHoliday ? 'holiday' : ''} ${isWeekend && !isHoliday ? 'weekend' : ''} ${isDisabled ? 'disabled' : ''}`}
                                                    disabled={isDisabled}
                                                    title={isHoliday ? `Holiday: ${holidayInfo.name}` : isDisabled ? 'Not available for booking' : isWeekend ? 'Weekend - Not available' : ''}
                                                >
                                                    <div className="date-btn-day">{dayName.toUpperCase()}</div>
                                                    <div className="date-btn-date-full">
                                                        <div className="date-number">{date.getDate()}</div>
                                                        <div className="date-month-year">{date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</div>
                                                    </div>
                                                    {!isHoliday && (
                                                        <div className="occupancy-info">
                                                            <div className="occupancy-item">
                                                                <div className="occupancy-label">Hot Desk Occupancy</div>
                                                                <div className="occupancy-value">{occupancyRate} %</div>
                                                            </div>
                                                            <div className="occupancy-item">
                                                                <div className="occupancy-label">Unit Occupancy Rate</div>
                                                                <div className="occupancy-value">{unitOccupancyRate} %</div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {isHoliday && (
                                                        <>
                                                            <div className="occupancy-info">
                                                                <div className="occupancy-item">
                                                                    <div className="occupancy-label">Hot Desk Occupancy</div>
                                                                    <div className="occupancy-value">{occupancyRate} %</div>
                                                                </div>
                                                                <div className="occupancy-item">
                                                                    <div className="occupancy-label">Unit Occupancy Rate</div>
                                                                    <div className="occupancy-value">{unitOccupancyRate} %</div>
                                                                </div>
                                                            </div>
                                                            <div className="date-btn-holiday-info" title={holidayInfo.name}>
                                                                ℹ️ {holidayInfo.name}
                                                            </div>
                                                        </>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {selectedDates.length > 0 && selectedDates.some(dateStr => {
                                        const ratesData = occupancyRates[dateStr] || {};
                                        const occupancyRate = ratesData.hotDeskOccupancy || 0;
                                        return occupancyRate >= 80;
                                    }) && (
                                            <div style={{
                                                backgroundColor: '#fff3cd',
                                                border: '1px solid #ffc107',
                                                borderRadius: '4px',
                                                padding: '12px',
                                                marginTop: '12px',
                                                color: '#856404',
                                                fontSize: '14px'
                                            }}>
                                                <strong>⚠️ High Demand:</strong> Some selected dates have occupancy ≥80%. These bookings may be added to a waiting list or flex pool.
                                            </div>
                                        )}
                                    <div className="selected-dates">
                                        <div>Date(s) Selected: {selectedDates.length} day(s)</div>
                                        {selectedDates.length > 0 && (
                                            <ol>
                                                {selectedDates.map((dateStr, index) => {
                                                    const [y, m, d] = dateStr.split('-').map(Number);
                                                    const date = new Date(y, m - 1, d);
                                                    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                                                    const month = date.toLocaleDateString('en-US', { month: 'short' });
                                                    const day = date.getDate();
                                                    const year = date.getFullYear();
                                                    return (
                                                        <li key={index}>{dayName}, {month} {day}, {year}</li>
                                                    );
                                                })}
                                            </ol>
                                        )}
                                    </div>
                                    <div className="multi-date-actions">
                                        <button
                                            onClick={handleMultipleDatesContinue}
                                            className="btn-submit"
                                            disabled={selectedDates.length < 2}
                                        >
                                            Continue to Desk Selection
                                        </button>
                                    </div>
                                    <div className="occupancy-info-legend">
                                        <p><strong>Hot Desk Occupancy:</strong> Count whole hot desks in office</p>
                                        <p><strong>Unit Occupancy Rate:</strong> Only count hot desks assigned to your unit</p>
                                    </div>
                                </div>

                                {/* Holiday Modal */}
                                {showHolidayModal && (
                                    <div className="holiday-modal-overlay" onClick={() => setShowHolidayModal(null)}>
                                        <div className="holiday-modal" onClick={(e) => e.stopPropagation()}>
                                            <div className="holiday-modal-header">
                                                <h3>🎉 Holiday</h3>
                                                <button className="holiday-modal-close" onClick={() => setShowHolidayModal(null)}>×</button>
                                            </div>
                                            <div className="holiday-modal-body">
                                                <p><strong>Date:</strong> {formatDisplayDate(showHolidayModal.holiday_date)}</p>
                                                <p><strong>Holiday:</strong> {showHolidayModal.name}</p>
                                                <p className="holiday-modal-note">This date is a public holiday. Booking on holidays is not recommended.</p>
                                            </div>
                                            <div className="holiday-modal-footer">
                                                <button className="btn-secondary" onClick={() => setShowHolidayModal(null)}>Cancel</button>
                                                <button
                                                    className="btn-primary"
                                                    onClick={() => {
                                                        const dateStr = showHolidayModal.holiday_date;
                                                        if (selectedDates.includes(dateStr)) {
                                                            handleMultipleDatesSelect(selectedDates.filter(d => d !== dateStr));
                                                        } else {
                                                            handleMultipleDatesSelect([...selectedDates, dateStr]);
                                                        }
                                                        setShowHolidayModal(null);
                                                    }}
                                                >
                                                    {selectedDates.includes(showHolidayModal.holiday_date) ? 'Deselect' : 'Book Anyway'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="wizard-nav">
                                    <button onClick={handleBack} className="btn-back-step">
                                        Back
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 6: Desk Selection */}
                        {step === 'desk-selection' && (() => {
                            return (
                                <div className="step">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                        <div>
                                            <h2>{getStepTitle('desk-selection', 'Select a Desk')}</h2>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                                            <button onClick={handleBack} className="btn-back-step" style={{ margin: 0 }}>Back</button>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: 'bold', color: isFunMode ? '#E91E63' : '#666' }}>
                                                <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '30px', height: '17px', margin: 0 }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isFunMode}
                                                        onChange={e => setIsFunMode(e.target.checked)}
                                                        style={{ opacity: 0, width: 0, height: 0 }}
                                                    />
                                                    <span style={{
                                                        position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                                                        backgroundColor: isFunMode ? '#E91E63' : '#ccc', transition: '.4s', borderRadius: '34px'
                                                    }}></span>
                                                    <span style={{
                                                        position: 'absolute', content: '""', height: '13px', width: '13px', left: isFunMode ? '14px' : '2px', bottom: '2px',
                                                        backgroundColor: 'white', transition: '.4s', borderRadius: '50%'
                                                    }}></span>
                                                </label>
                                                <span>Fun Mode</span>
                                            </div>
                                        </div>
                                    </div>
                                    {selectedDates.length > 1 ? (
                                        <p className="step-info">
                                            Day {currentDateIndex + 1} of {selectedDates.length}: {formatDisplayDate(activeDate || deskSelectionDate)}
                                        </p>
                                    ) : (
                                        <p className="step-info">
                                            Booking for {formatDisplayDate(activeDate || deskSelectionDate)}
                                            {bookingType === 'half-day' && timeSlot ? ` • ${timeSlot}` : ''}
                                            {userMonitorPref && userMonitorPref !== 'Any' ? ` • ${userMonitorPref}` : ''}
                                        </p>
                                    )}

                                    {!hasPreferredMonitor && userMonitorPref !== 'Any' && (
                                        <div className="monitor-warning">
                                            Preferred {userMonitorPref} desks are not available. Showing available alternatives.
                                        </div>
                                    )}

                                    {selectedDates.length > 1 && (
                                        <div className="assignment-progress">
                                            <span>{assignedCount}/{selectedDates.length} days assigned</span>
                                        </div>
                                    )}
                                    {assignmentList.length > 0 && (
                                        <ul className="assignment-summary">
                                            {assignmentList.map(({ date, desk, index }) => (
                                                <li
                                                    key={date}
                                                    className={`assignment-item ${index === currentDateIndex ? 'active' : desk ? 'completed' : 'pending'}`}
                                                >
                                                    <strong>Day {index + 1}:</strong> {formatDisplayDate(date)} —{' '}
                                                    {desk ? `Desk ${desk.id}` : 'Pending selection'}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {loading ? (
                                        <div className="loading-state">Loading desks...</div>
                                    ) : desks.length === 0 ? (
                                        <div className="loading-state">
                                            <p>No desks available for your unit. Please contact your administrator.</p>
                                            <div className="wizard-nav" style={{ marginTop: '20px' }}>
                                                <button onClick={handleBack} className="btn-back-step">
                                                    Back
                                                </button>
                                            </div>
                                        </div>
                                    ) : (() => {
                                        // Get all user's units (primary + additional)
                                        const allUserUnits = [user?.unit].filter(Boolean);
                                        if (user?.additional_units) {
                                            allUserUnits.push(...user.additional_units.split(',').map(u => u.trim()).filter(Boolean));
                                        }
                                        // Remove duplicates
                                        const uniqueUnits = [...new Set(allUserUnits)];

                                        return (
                                            <>
                                                {uniqueUnits.map((unitName, unitIndex) => {
                                                    const unitDesks = desks.filter(d => d.unit === unitName);
                                                    if (unitDesks.length === 0) return null;

                                                    return (
                                                        <div key={unitName} style={{ marginBottom: unitIndex < uniqueUnits.length - 1 ? '40px' : '0' }}>
                                                            {uniqueUnits.length > 1 && (
                                                                <h3 style={{
                                                                    color: '#0072CE',
                                                                    marginBottom: '15px',
                                                                    paddingBottom: '10px',
                                                                    borderBottom: '2px solid #e0e8f0',
                                                                    fontSize: '1.2rem'
                                                                }}>
                                                                    📍 {unitName}
                                                                </h3>
                                                            )}
                                                            <DeskMap
                                                                desks={unitDesks}
                                                                selectedDate={deskSelectionDate}
                                                                bookingsForDate={bookingsByDate[deskSelectionDate] || []}
                                                                onDeskClick={handleDeskSelect}
                                                                userEmail={user?.email || ''}
                                                                userUnit={unitName}
                                                                selectedDeskId={activeDeskId}
                                                                monitorPreference={user.monitor_preference || 'Single Monitor'}
                                                                funMode={isFunMode}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        );
                                    })()
                                    }
                                    {desks.length > 0 && (
                                        <div className="wizard-nav" style={{ marginTop: '20px' }}>
                                            {selectedDates.length > 1 && currentDateIndex < selectedDates.length - 1 && deskAssignments[activeDate] && (
                                                <button
                                                    onClick={() => {
                                                        const nextDate = selectedDates[currentDateIndex + 1];
                                                        if (nextDate) {
                                                            loadBookingsForDate(nextDate);
                                                        }
                                                        setCurrentDateIndex((prev) => prev + 1);
                                                    }}
                                                    className="btn-submit"
                                                    style={{ marginLeft: 'auto' }}
                                                >
                                                    Next Day
                                                </button>
                                            )}
                                            {(selectedDates.length === 1 || currentDateIndex >= selectedDates.length - 1) && deskAssignments[activeDate] && (
                                                <button
                                                    onClick={() => setStep('confirm')}
                                                    className="btn-submit"
                                                    style={{ marginLeft: 'auto' }}
                                                >
                                                    Continue to Confirm
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                        {/* Step 7: Confirm Booking */}
                        {step === 'confirm' && allDatesAssigned && (
                            <div className="step">
                                <h2>{getStepTitle('confirm', 'Confirm Your Booking')}</h2>
                                <div className="confirm-details">
                                    <div className="detail-section">
                                        <h3>Desk Assignments</h3>
                                        <ul className="assignment-list">
                                            {assignmentList.map(({ date, desk, index }) => (
                                                <li key={date}>
                                                    <strong>Day {index + 1}:</strong> Desk {desk.id} • {desk.zone} • {(desk.unit || user.unit || 'Unit')}<br />
                                                    <span className="assignment-date">{formatDisplayDate(date)}</span>
                                                    {desk.equipment && (
                                                        <span className="assignment-equipment"> – Equipment: {desk.equipment}</span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="detail-section">
                                        <h3>Booking Details</h3>
                                        {bookingType === 'half-day' ? (
                                            <>
                                                <p><strong>Type:</strong> Half Day</p>
                                                <p><strong>Time Slot:</strong> {timeSlot}</p>
                                                <p><strong>Date:</strong> {formatDisplayDate(selectedDates[0])}</p>
                                            </>
                                        ) : (
                                            <>
                                                <p><strong>Type:</strong> Full Day</p>
                                                <p><strong>Dates:</strong> {selectedDates.map(formatDisplayDate).join(', ')}</p>
                                                <p><strong>Duration:</strong> {selectedDates.length} day(s)</p>
                                            </>
                                        )}
                                    </div>

                                </div>
                                <div className="confirm-buttons">
                                    <button onClick={handleConfirmBooking} className="btn-confirm" disabled={loading}>
                                        {loading ? 'Confirming...' : 'Confirm Booking'}
                                    </button>
                                    <button onClick={handleBack} className="btn-back-step">
                                        Back
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 8: Booking Ticket */}
                        {step === 'ticket' && allDatesAssigned && (() => {
                            const ticketData = generateTicket();
                            return (
                                <div className="step">
                                    <h2>✅ Booking Confirmed!</h2>
                                    <div className="flight-ticket">
                                        <div className="ticket-left">
                                            <div className="employee-info">
                                                <div className="employee-name">{ticketData.employeeName}</div>
                                                <div className="employee-unit">{ticketData.unit}</div>
                                            </div>
                                            <div className="duration-info">
                                                <div className="label">Duration</div>
                                                <div className="value">{ticketData.duration}</div>
                                            </div>
                                            <div className="desks-info">
                                                <div className="label">Desk Reserved</div>
                                                <div className="value large">{ticketData.desksReserved}</div>
                                            </div>
                                            <div className="date-range">
                                                <div className="date-section">
                                                    <div className="label">From</div>
                                                    <div className="date-value">{new Date(ticketData.fromDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                                                </div>
                                                <div className="date-section">
                                                    <div className="label">To</div>
                                                    <div className="date-value">{new Date(ticketData.toDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                                                </div>
                                            </div>
                                            <div className="barcode">
                                                <div className="barcode-lines">
                                                    {Array.from({ length: 40 }).map((_, i) => (
                                                        <div key={i} className={`bar ${i % 3 === 0 ? 'thick' : 'thin'}`}></div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="ticket-divider"></div>
                                        <div className="ticket-right">
                                            <div className="unicef-header">
                                                <div className="unicef-text">UNICEF INDONESIA</div>
                                                <div className="unicef-logo">
                                                    <img src="/data/UNICEF_Logo.png"
                                                        alt="UNICEF Logo"
                                                        style={{ height: '40px', width: 'auto' }} />
                                                </div>
                                            </div>
                                            <div className="assignment-table">
                                                <div className="table-header">
                                                    <div className="col-header">Date</div>
                                                    <div className="col-header">Desk</div>
                                                    <div className="col-header">Zone</div>
                                                    <div className="col-header">Monitor</div>
                                                </div>
                                                {ticketData.assignments.map((assignment, index) => (
                                                    <div key={index} className="table-row">
                                                        <div className="col-data">{new Date(assignment.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                                                        <div className="col-data">{assignment.desk}</div>
                                                        <div className="col-data">{assignment.zone}</div>
                                                        <div className="col-data">{assignment.monitor}</div>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="booking-ref">
                                                <div className="ref-label">Booking Reference:</div>
                                                <div className="ref-value">{ticketData.bookingRef}</div>
                                            </div>
                                            <div className="ticket-footer">
                                                Please keep this ticket for your records
                                            </div>
                                        </div>
                                    </div>
                                    <div className="ticket-buttons">
                                        <button onClick={handlePrint} className="btn-print">
                                            🖨️ Print
                                        </button>
                                        <button onClick={handleBack} className="btn-new-booking">
                                            ➕ New Booking
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </ErrorBoundary>
            </div >
        </div >
    );
}

export default BookingWizard;
